import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { compact, isEmpty } from 'lodash-es';
import { DealershipsEntities } from 'client/data/models/dealerships';
import { createNicePhone, clearPhone } from 'site-modules/shared/utils/phone-format';
import { getDealerPhone } from 'site-modules/shared/utils/dealer-details-utils';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { Link } from 'site-modules/shared/components/link/link';
import { ClickableCard } from 'site-modules/shared/components/inventory/clickable-card/clickable-card';
import { ClickableCardLink } from 'site-modules/shared/components/inventory/clickable-card/clickable-card-link/clickable-card-link';
import { numberWithCommas } from 'site-modules/shared/utils/string';

import './dealer-card.scss';

export function DealerCard({ dealer, isMobile = false }) {
  const {
    name,
    address = {},
    phoneNumbers = {},
    salesRating = 0,
    salesReviewCount = 0,
    showroomPage = '',
    productFeatures = {},
    fiveStarDealerAward,
    distance,
  } = dealer;
  const { street, city, stateCode, zip } = address;
  const dealerPhone = getDealerPhone({ phoneNumbers, isDirectDealer: productFeatures.directDealer });
  const hasFiveStarDealerAward = !!(fiveStarDealerAward && fiveStarDealerAward.length);
  const fiveStarEl = (
    <>
      <i className="icon-5star me-0_25" title="Five Star Dealer" />
      <span className="size-14 fw-medium">Five Star Dealer</span>
    </>
  );
  const dealerData = compact([
    !!distance && `${numberWithCommas(distance)} mi away`,
    !isEmpty(address) && `${street}, ${city}, ${stateCode} ${zip}`,
  ]);

  return (
    <ClickableCard tag="li" className="dealer-card p-1_5 rounded-16 mb-1 bg-white d-flex justify-content-between">
      <div className="col">
        {isMobile && hasFiveStarDealerAward && <div className="text-blue-30 mb-0_5">{fiveStarEl}</div>}
        <h2 className="text-cool-gray-10 size-16 mb-0_25">
          <ClickableCardLink to={showroomPage} data-tracking-id="click_view_dealer_page">
            <div>{name}</div>
          </ClickableCardLink>
        </h2>
        <div className="mb-0_75 d-flex align-items-center">
          {!!salesReviewCount && (
            <>
              <RatingStars rating={salesRating} starClass="text-blue-50 size-16" />
              <span className="size-14 text-cool-gray-40 ms-0_25">({salesReviewCount})</span>
              <PopoverItem
                className="text-gray-darker"
                iconClassName="small position-relative z-3 align-middle me-0_25"
                popoverClassName="dealer-card-sales-rating-popover"
                innerClassName="px-0_75 py-0_5 text-cool-gray-30"
                placement="top"
                screenReaderTooltipLabel="sales rating"
                trigger="click hover focus"
              >
                Based on reviews posted in the prior 24 months.
              </PopoverItem>
            </>
          )}
          {!isMobile && hasFiveStarDealerAward && <div className="text-blue-30">{fiveStarEl}</div>}
        </div>
        {!isEmpty(dealerData) && (
          <div className="text-cool-gray-40 mb-0_75">
            {dealerData.map((linkData, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={`data-${index}`}>
                {!!index && ' | '}
                {linkData}
              </Fragment>
            ))}
          </div>
        )}
        {isMobile && (
          <Button
            tag="a"
            href={showroomPage}
            className="dealer-card-btn-mobile size-16 rounded-8 text-transform-none my-1_5 d-flex justify-content-center align-items-center"
            color="blue-50"
            outline
            block
            data-tracking-parent="edm-entry-dealer-list"
            data-tracking-id="click_view_dealer_page"
          >
            Dealer Profile
          </Button>
        )}
        <div className={classNames('d-flex align-items-center', { 'justify-content-between': isMobile })}>
          {dealerPhone && (
            <>
              <div>
                <i className="icon-mobile size-14 text-cool-gray-40 me-0_5" aria-hidden />
                <a
                  href={`tel:${clearPhone(dealerPhone)}`}
                  data-tracking-id="ddp_call_dealer"
                  className="text-decoration-underline text-cool-gray-30"
                  data-tracking-ignore-value
                >
                  <span>{createNicePhone(dealerPhone)}</span>
                </a>
              </div>
              <div className="text-cool-gray-60 mx-1">|</div>
            </>
          )}
          <Link
            to={`${showroomPage.replace('/all/', '/')}/create-sales-review.html`}
            className="text-decoration-underline text-cool-gray-30"
            data-tracking-parent="dealerships_write_review_link"
            data-tracking-id="click_write_dealer_review"
          >
            <i className="icon-pencil size-14 text-cool-gray-40 me-0_5" aria-hidden />
            Write a review
          </Link>
        </div>
      </div>
      {!isMobile && (
        <Button
          tag="a"
          href={showroomPage}
          className="dealer-card-btn-desktop size-16 bg-blue-90 d-flex justify-content-center align-items-center align-self-center d-block"
          data-tracking-parent="edm-entry-dealer-list"
          data-tracking-id="click_view_dealer_page"
          aria-label={name}
        >
          <i className="icon-arrow-right3 text-blue-20" aria-hidden />
        </Button>
      )}
    </ClickableCard>
  );
}

DealerCard.propTypes = {
  dealer: DealershipsEntities.Dealer.isRequired,
  isMobile: PropTypes.bool,
};
